import { Settings, X, RotateCcw, LogOut, Share2, Volume2, VolumeX, Lightbulb, Music, Sword, Smartphone } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { shareNicaBros } from "@/lib/share";
import { hapticsSupported, hapticsEnabled, setHapticsEnabled } from "@/game/haptics";

// In-game settings panel: sound toggle, music/SFX volume, restart, exit and share.
export default function SettingsMenu({ open, onClose, muted, onToggleSound, musicVol, sfxVol, onMusicVol, onSfxVol, onRestart, onExit, onSuggest }) {
  const { t } = useLang();
  const [haptics, setHaptics] = useState(() => hapticsEnabled());

  const doShare = async () => {
    const res = await shareNicaBros({ text: t("shareText") });
    if (res === "copied") toast.success(t("linkCopied"));
  };

  if (!open) return null;

  const Slider = ({ icon: Icon, label, value, onChange, testid, color }) => (
    <div className="bg-[#222] rounded-lg px-3 py-2" data-testid={`${testid}-row`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-pixel text-[10px] text-white flex items-center gap-2"><Icon size={15} style={{ color }} /> {label}</span>
        <span className="font-pixel text-[10px] text-white/70">{Math.round(value * 100)}%</span>
      </div>
      <input
        data-testid={testid}
        type="range" min="0" max="1" step="0.05" value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full cursor-pointer"
        style={{ accentColor: color }}
        aria-label={label}
      />
    </div>
  );

  return (
    <div className="absolute inset-0 z-[65] overflow-y-auto overscroll-contain flex items-start justify-center bg-black/85 p-3 scanlines" data-testid="settings-overlay">
      <div className="pixel-card bg-[#141414] p-4 w-full max-w-sm max-h-full overflow-y-auto pop-in my-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-pixel text-base md:text-lg text-[#FFEB3B] pixel-stroke flex items-center gap-2">
            <Settings size={18} strokeWidth={2.5} /> {t("settings")}
          </h2>
          <button data-testid="settings-close-btn" onClick={onClose} className="pixel-btn bg-black/55 text-white p-2">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {/* Master sound toggle */}
          <button data-testid="settings-sound-btn" onClick={onToggleSound} className="pixel-btn bg-[#222] text-white px-4 py-2.5 text-xs flex items-center justify-between">
            <span className="flex items-center gap-2">{muted ? <VolumeX size={16} /> : <Volume2 size={16} />} {t("soundLabel")}</span>
            <span className={muted ? "text-white/50" : "text-[#4CAF50]"}>{muted ? t("soundOff") : t("soundOn")}</span>
          </button>

          {/* Independent volume sliders */}
          <Slider icon={Music} label={t("musicVolume")} value={musicVol} onChange={onMusicVol} testid="settings-music-volume" color="#0067C6" />
          <Slider icon={Sword} label={t("sfxVolume")} value={sfxVol} onChange={onSfxVol} testid="settings-sfx-volume" color="#FF9800" />

          {/* Haptic feedback toggle (mobile) */}
          {hapticsSupported() && (
            <button data-testid="settings-haptics-btn" onClick={() => { const v = !haptics; setHaptics(v); setHapticsEnabled(v); }} className="pixel-btn bg-[#222] text-white px-4 py-2.5 text-xs flex items-center justify-between">
              <span className="flex items-center gap-2"><Smartphone size={16} /> {t("hapticsLabel")}</span>
              <span className={haptics ? "text-[#4CAF50]" : "text-white/50"}>{haptics ? t("soundOn") : t("soundOff")}</span>
            </button>
          )}

          <button data-testid="settings-restart-btn" onClick={onRestart} className="pixel-btn bg-[#FF5722] text-white px-4 py-2.5 text-xs flex items-center gap-2 justify-center">
            <RotateCcw size={16} strokeWidth={2.5} /> {t("restartLevel")}
          </button>

          <button data-testid="settings-share-btn" onClick={doShare} className="pixel-btn bg-[#0067C6] text-white px-4 py-2.5 text-xs flex items-center gap-2 justify-center">
            <Share2 size={16} strokeWidth={2.5} /> {t("shareGame")}
          </button>

          <button data-testid="settings-suggest-btn" onClick={onSuggest} className="pixel-btn bg-[#FFB300] text-black px-4 py-2.5 text-xs flex items-center gap-2 justify-center">
            <Lightbulb size={16} strokeWidth={2.5} /> {t("suggestIdeas")}
          </button>

          <button data-testid="settings-exit-btn" onClick={onExit} className="pixel-btn bg-[#444] text-white px-4 py-2.5 text-xs flex items-center gap-2 justify-center">
            <LogOut size={16} strokeWidth={2.5} /> {t("exitToMenu")}
          </button>
        </div>
      </div>
    </div>
  );
}
